import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse, Mattslag } from "~app/common/models/constants";
import { HandelssortimentDTO } from "~app/common/models/handelssortiment/HandelssortimentDTO";
import { HandelssortimentResponseDTO } from "~app/common/models/handelssortiment/HandelssortimentResponseDTO";
import { HandelssortimentSortimentDTO } from "~app/common/models/handelssortiment/HandelssortimentSortimentDTO";
import { RavaruHandelssortimentDTO } from "~app/common/models/handelssortiment/RavaruHandelssortimentDTO";
import { ModelActionService } from "~app/common/services/cqs/model-action.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { RaskTitleService } from "~app/common/services/title.service";
import { CommonTexts } from "~app/common/texts/common.texts";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IHandelssortimentProdukt } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { HandelssortimentService } from "../handelssortiment.service";
import { HandelssortimentValidation } from "./handelssortiment.validation";
import { HeaderComponent } from "./header/header.component";
import { HsProduktComponent } from "./hs-produkt/hs-produkt.component";
import { MatningComponent } from "./matning/matning.component";
import { SortimentComponent } from "./sortiment/sortiment.component";

@Component({
    selector: "rask-ny",
    styleUrls: ["./details.component.scss"],
    templateUrl: "./details.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        ActionButtonComponent,
        HeaderComponent,
        SortimentComponent,
        MatningComponent,
        HsProduktComponent,
        SaveButtonComponent,
        TranslatePipe
    ]
})
export class DetailsComponent implements OnInit {
    commonTexts = CommonTexts.texts;
    defaultMattslag = Mattslag.M3F;
    foradlingsHandelssortiment: RavaruHandelssortimentDTO[];
    form: UntypedFormGroup;
    handelssortiment: HandelssortimentDTO;
    handelssortimentId?: number;
    handelssortimentSortiment: HandelssortimentSortimentDTO;
    inNewMode = true;
    productsDirty = false;
    produkter: IHandelssortimentProdukt[] = [];
    refereradFranKundorder = false;
    relateradeHandelssortiment: HandelssortimentDTO[];
    validationContext: IValidationContext;

    private readonly $translate = inject(TranslateService);
    private readonly busy = inject(BusyService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly formBinder = inject(FormBinderService);
    private readonly handelssortimentService = inject(HandelssortimentService);
    private readonly modalService = inject(RaskModalService);
    private readonly modelActionService = inject(ModelActionService);
    private readonly raskTitleService = inject(RaskTitleService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);

    canSave(): boolean {
        return !!this.form &&
            !this.form.disabled &&
            (this.form.dirty || this.productsDirty) &&
            this.fromDateLesserThanTomDate() && this.validationContext.validate();
    }

    createNewSortiment() {
        this.handelssortiment = {
            aktiv: true,
            ocertifierat: false,
            beskrivning: undefined,
            foreslagetMattslag: this.defaultMattslag,
            giltigFrom: undefined,
            giltigTom: undefined,
            handelssortimentsgruppId: undefined,
            huvudsortimentId: undefined,
            id: undefined,
            lagerenhet: this.defaultMattslag,
            namn: undefined,
            ravaruHandelssortimentId: undefined,
            relateratHandelssortimentId: undefined,
            viol3HandelssortimentInfo: undefined,
            inmatningshandelssortimentId: undefined,
            forProduktion: true,
            forInmatning: false,
            densitetSkotning: 800
        } as HandelssortimentDTO;

        this.handelssortimentSortiment = {
            huvudsortimentId: undefined,
            sortimentIdLista: []
        };

        this.produkter = [];

        this.modelActionService.addNewModel(this.handelssortiment, "Handelssortiment");
        this.modelActionService.addNewChildModel(this.handelssortimentSortiment, "HandelssortimentSortiment", this.handelssortiment);
        this.setupForm();
    }

    deleteHandelssortiment() {
        const header = this.$translate.instant("HANDELSSORTIMENT_DETAIL_DELETE_PROMPT_HEADER");
        const text = this.$translate.instant("HANDELSSORTIMENT_DETAIL_DELETE_PROMPT_TEXT");
        this.modalService.confirm(new ModalSettings(header, text)).subscribe(modalResult => {
            if (modalResult === ConfirmDialogResponse.save) {
                this.modelActionService.removeModel(this.handelssortiment, "Handelssortiment");
                const worker = this.modelActionService.pushCommandQueue();
                this.busy.addWork(worker);
                worker.subscribe(r => {
                    this.router.navigate(["/handelssortiment"]);
                });
            }
        });
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        // Gör inget
    }

    fromDateLesserThanTomDate(): boolean {
        if (this.form.get("giltigTom").value) {
            return (this.form.get("giltigFrom").value < this.form.get("giltigTom").value);
        }
        return true;
    }

    getTitle(): string {
        if (this.inNewMode) {
            return "HANDELSSORTIMENT_DETAIL_SKAPA_NY";
        }
        return "HANDELSSORTIMENT_DETAIL_EDITERA";
    }

    handleRouteParamChange() {
        this.route.paramMap.subscribe(p => {
            this.handelssortimentId = +this.route.snapshot.paramMap.get("id");
            if (!isNaN(this.handelssortimentId)) { // load existing
                this.loadHandelssortiment();
                this.inNewMode = false;
            } else { // new mode
                this.createNewSortiment();
            }
        });
    }

    isNew() {
        return !!this.handelssortiment && !this.handelssortiment.id;
    }

    loadHandelssortiment() {
        this.handelssortimentService.getHandelssortimentById(this.handelssortimentId).subscribe(r => {
            this.handelssortiment = this.mapHandelsortiment(r);
            this.relateradeHandelssortiment = r.relateradeHandelssortiment.map(rh => this.mapHandelsortiment(rh as any));
            this.refereradFranKundorder = r.isRefereratFranKundorderrad;
            this.foradlingsHandelssortiment = r.foradlingsHandelssortiment;
            this.handelssortimentSortiment = {
                huvudsortimentId: r.huvudsortimentId,
                sortimentIdLista: r.sortimentIdLista
            };

            this.produkter = r.handelssortimentProdukter;

            this.modelActionService.bindModel(this.handelssortiment, "Handelssortiment");
            this.modelActionService.bindChildModel(this.handelssortimentSortiment, "HandelssortimentSortiment", this.handelssortiment);
            this.modelActionService.bindChildModel(this.produkter, "HandelssortimentProdukt", this.handelssortiment);
            this.setupForm();
        });
    }

    mapHandelsortiment(r: HandelssortimentResponseDTO) {
        return {
            aktiv: r.aktiv,
            ocertifierat: r.ocertifierat,
            beskrivning: r.beskrivning,
            beteckning: r.beteckning,
            foreslagetMattslag: r.foreslagetMattslag,
            densitetSkotning: r.densitetSkotning,
            giltigFrom: r.giltigFrom,
            giltigTom: r.giltigTom,
            handelssortimentsgruppId: r.handelssortimentsgruppId,
            huvudsortimentId: r.huvudsortimentId,
            id: r.id,
            lagerenhet: r.lagerenhet,
            namn: r.namn,
            ravaruHandelssortimentId: r.ravaruHandelssortiment?.id,
            relateratHandelssortimentId: r.relateratHandelssortiment?.id,
            inmatningshandelssortimentId: r.inmatningshandelssortiment?.id,
            rowVersion: r.rowVersion,
            skapadAv: r.skapadAv,
            viol3HandelssortimentInfo: r.viol3HandelssortimentInfo,
            forInmatning: r.forInmatning,
            forProduktion: r.forProduktion
        } as HandelssortimentDTO;
    }

    markAsPristine() {
        this.form.markAsPristine();
        this.productsDirty = false;
    }

    ngOnInit() {
        this.modelActionService.reset();
        this.handleRouteParamChange();
    }

    productsChanged() {
        this.form.markAsDirty();
        this.productsDirty = true;
        this.rebuildValidationContext();
    }

    rebuildValidationContext() {
        this.validationContext = HandelssortimentValidation.init(
            this.handelssortiment,
            this.produkter);
    }

    save = () => {
        const saveObs = this.modelActionService.pushCommandQueue();
        this.busy.addWork(saveObs)
            .subscribe(r => {
                this.toastService.add("DEFAULT_CHANGE_SUCCESS");
                const isNewMode = this.inNewMode;
                if (this.inNewMode) {
                    this.inNewMode = false;
                    this.router.navigate([
                        "/handelssortiment",
                        this.handelssortiment.id
                    ]);
                }

                if (isNewMode) {
                    this.handelssortimentId = this.handelssortiment.id;
                }
                this.modelActionService.reset();
                this.loadHandelssortiment();
                this.markAsPristine();
            });
    };

    setupForm() {
        this.updateTitle();
        this.form = this.formBinder.CreateGroup(this.handelssortiment);
        this.rebuildValidationContext();
        if (this.refereradFranKundorder) {
            this.form.get("relateratHandelssortimentId").disable();
        }
        this.dirtyFormService.onDirty(this.form, this.save.bind(this), d => this.dirtyCheckCallback(d));
        this.dirtyFormService.ngModelValidationFn = () => this.canSave();
    }

    private updateTitle(): void {
        if (this.isNew()) {
            this.raskTitleService.setTitle("Skapa nytt handelssortiment");
        } else {
            this.raskTitleService.setTitle(`${this.handelssortiment?.namn}`);
        }
    }
}
